import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import AuthCallback from "./components/AuthCallback";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Contacts from "./pages/Contacts";
import ContactDetail from "./pages/ContactDetail";
import Properties from "./pages/Properties";
import PropertyDetail from "./pages/PropertyDetail";
import Leads from "./pages/Leads";
import Pipeline from "./pages/Pipeline";
import Tasks from "./pages/Tasks";
import Agenda from "./pages/Agenda";
import CalendarPage from "./pages/CalendarPage";
import Communications from "./pages/Communications";
import Prospecting from "./pages/Prospecting";
import Transactions from "./pages/Transactions";
import Documents from "./pages/Documents";
import Reports from "./pages/Reports";
import Marketing from "./pages/Marketing";
import AIAssistant from "./pages/AIAssistant";
import Settings from "./pages/Settings";
import { Building2, Loader2 } from "lucide-react";
import { Component } from "react";

class PageBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  componentDidUpdate(prev) { if (prev.pathKey !== this.props.pathKey && this.state.error) this.setState({ error: null }); }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="max-w-md mx-auto mt-16 bg-white rounded-2xl p-6 shadow-sm text-center" data-testid="page-error">
        <p className="font-display font-bold text-navy-800">Something went wrong while drawing this page.</p>
        <p className="text-xs text-slate-500 mt-1 break-words">{String(this.state.error?.message || this.state.error)}</p>
        <button onClick={() => this.setState({ error: null })} className="mt-3 text-sm bg-navy-800 text-white rounded-lg px-4 py-1.5" data-testid="page-error-retry">Try again</button>
      </div>
    );
  }
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center"><Building2 className="w-5 h-5 text-navy-900" /></div>
        <Loader2 className="w-5 h-5 animate-spin text-navy-700" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Layout><PageBoundary pathKey={window.location.pathname + window.location.search}>{children}</PageBoundary></Layout>;
}

function Router() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/contacts" element={<Protected><Contacts /></Protected>} />
      <Route path="/contacts/:id" element={<Protected><ContactDetail /></Protected>} />
      <Route path="/properties" element={<Protected><Properties /></Protected>} />
      <Route path="/properties/:id" element={<Protected><PropertyDetail /></Protected>} />
      <Route path="/leads" element={<Protected><Leads /></Protected>} />
      <Route path="/pipeline" element={<Protected><Pipeline /></Protected>} />
      <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
      <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/agenda" element={<Protected><Agenda /></Protected>} />
      <Route path="/communications" element={<Protected><Communications /></Protected>} />
      <Route path="/prospecting" element={<Protected><Prospecting /></Protected>} />
      <Route path="/transactions" element={<Protected><Transactions /></Protected>} />
      <Route path="/documents" element={<Protected><Documents /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/marketing" element={<Protected><Marketing /></Protected>} />
      <Route path="/ai" element={<Protected><AIAssistant /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Router />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
